<script setup lang="ts">
import roleAssignAccountType from "@/types/responses/roleAssignAccount"
import organizationType from "@/types/requests/organizationType"
import { computed } from "vue"
import i18n from "@/lang/i18n"

const emit = defineEmits(['update:modelValue'])
const props = defineProps<{
  title: string
  accounts: roleAssignAccountType[]
  modelValue: roleAssignAccountType[]
  alreadyAssignedAccountIds: number[]
  organizations: organizationType[]
  displayIcon: boolean
  loading: boolean
}>()

const modelValue = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit("update:modelValue", value)
  }
})

const organization = computed(() => (account: roleAssignAccountType) => {
  return props.organizations.find((organization: organizationType) => {
    return organization.type == account.officeable_type && organization.id == account.officeable_id
  })?.name
})

const accountFindIndex = computed(() => (account: roleAssignAccountType) => {
  return modelValue.value.findIndex((value) => value.id === account.id)
})

const isSelected = computed(() => (account: roleAssignAccountType, index: number) => {
  const accountIndex = accountFindIndex.value(account)
  if (accountIndex === -1) {
    return {
      backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#ffffff'
    }
  }
  return {
    backgroundColor: '#007bff',
    color: '#ffffff'
  }
})

const displayEmail = computed(() => (account: roleAssignAccountType) => {
  const originalEmail = account.email ?? ''
  if (!originalEmail) return originalEmail
  const domain = originalEmail.split('@')[1]
  return `${originalEmail.slice(0, 3)}***@${domain}`
})

function addOrdelete(account: roleAssignAccountType) {
  const accountIndex = accountFindIndex.value(account)
  if (accountIndex === -1) {
    modelValue.value.push(account)
  } else {
    modelValue.value.splice(accountIndex, 1)
  }
}

function isAlreadyAssigned(account: roleAssignAccountType) {
  if (!props.displayIcon) return true
  return !!props.alreadyAssignedAccountIds?.find((id: number) => {
    return id == account.id
  })
}
</script>
<template>
  <div
    class="w-100 position-relative"
    style="height:400px; border: 1px solid #adb5bd; border-radius: 5px; overflow-y: auto;"
  >
    <template v-if="props.loading">
      <div class="accounts-loading h-100">
        <label>
          {{ i18n.global.t('pages.common.list.reading') }}
        </label>
      </div>
    </template>
    <div class="d-flex justify-center mt-1">
      <h6 style="font-weight: bold; font-size: 14px;" class="pt-2">
        {{ props.title }}
      </h6>
    </div>
    <v-row
      class="mx-3 mt-6"
      style="border-bottom: 1px solid #adb5bd;"
    >
      <v-col cols="1">
        {{ i18n.global.t('pages.roleAssignment.form.header.id') }}
      </v-col>
      <v-col cols="3">
        {{ i18n.global.t('pages.roleAssignment.form.header.organization') }}
      </v-col>
      <v-col cols="4">
        {{ i18n.global.t('pages.roleAssignment.form.header.userName') }}
      </v-col>
      <v-col cols="4">
        {{ i18n.global.t('pages.roleAssignment.form.header.email') }}
      </v-col>
    </v-row>
    <v-row
      v-for="(account, index) in props.accounts" :key="index" :value="account"
      class="mx-3"
      :style="isSelected(account, index)"
      @click="addOrdelete(account)"
      role="button"
    >
      <v-col cols="1">
        {{ account.id }}
      </v-col>
      <v-col cols="3">
        {{ organization(account) }}
      </v-col>
      <v-col
        cols="4"

        style="word-break: break-all;"
      >
        {{ account.full_name }}
      </v-col>
      <v-col
        cols="4"

        style="word-break: break-all;"
      >
        {{ displayEmail(account) }}
        <v-icon
          v-if="props.displayIcon && !isAlreadyAssigned(account)" icon="mdi-new-box"
        />
      </v-col>
    </v-row>
  </div>
</template>
<style scoped>
.accounts-loading {
  height: 100%;
  position: absolute;
  z-index: 1000;
  background-color: rgba(80,80,80,0.5);
  right: 0;
  left: 0;
}
.accounts-loading>label {
  font-weight: bold;
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
}
</style>
